import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Users, Award, CalendarClock, Inbox, LayoutDashboard, Loader2, Plus, X, Search, Check, XCircle, Cake, Bell, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";
import { useAuth } from "../../context/AuthContext";
import EmployeeProfileCard from "./EmployeeProfileCard";

const ease = [0.16, 1, 0.3, 1];
const LEADERSHIP = ["admin", "ceo", "director"];

function statusBadge(t, status) {
  const map = {
    pending: ["badge-amber", t("os.people.statusPending")],
    approved: ["badge-emerald", t("os.people.statusApproved")],
    rejected: ["badge-danger", t("os.people.statusRejected")],
    open: ["badge-violet", t("os.people.statusOpen")],
    in_progress: ["badge-amber", t("os.people.statusInProgress")],
    resolved: ["badge-emerald", t("os.people.statusResolved")],
  };
  const [cls, label] = map[status] || ["badge-muted", status];
  return <span className={`badge ${cls}`}>{label}</span>;
}

/* ============ INICIO / HOME ============ */
function HomeTab({ home, onGo }) {
  const { t } = useI18n();
  const b = home.timeoff_balance || {};
  const kpis = [
    { label: t("os.people.available"), value: `${b.available ?? "—"}`, sub: `${b.used ?? 0} ${t("os.people.used")} / ${b.allowance ?? 0}`, icon: CalendarClock },
    { label: t("os.people.pendingTimeoff"), value: home.pending_timeoff ?? 0, icon: CalendarClock },
    { label: t("os.people.openRequests"), value: home.open_requests ?? 0, icon: Inbox },
    { label: t("os.people.unread"), value: home.unread_notifications ?? 0, icon: Bell },
  ];
  return (
    <div className="space-y-8" data-testid="people-home">
      <EmployeeProfileCard />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k, i) => (
          <motion.div key={k.label} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease, delay: i * 0.05 }} className="card-surface p-5" data-testid={`people-kpi-${i}`}>
            <div className="flex items-center gap-2 overline text-navy/40"><k.icon className="w-3.5 h-3.5" /> {k.label}</div>
            <div className="mt-3 text-3xl font-medium tracking-tight">{k.value}</div>
            {k.sub && <div className="mt-1 text-xs text-navy/45">{k.sub}</div>}
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card-surface p-6">
          <div className="overline text-navy/40 mb-5 flex items-center gap-2"><Award className="w-3.5 h-3.5" /> {t("os.people.myRecognitions")}</div>
          {(home.recognitions || []).length === 0 ? (
            <p className="text-sm text-navy/35" data-testid="people-no-recognition">{t("os.people.noRecognitions")}</p>
          ) : (
            <div className="space-y-4">
              {home.recognitions.map((r, i) => (
                <div key={i} className="flex gap-3" data-testid={`people-recognition-${i}`}>
                  <div className="text-xl shrink-0">{(r.badge || "").split(" ")[0]}</div>
                  <div>
                    <p className="text-sm text-navy/80">{r.badge}</p>
                    <p className="text-sm text-navy/55 leading-snug">"{r.message}"</p>
                    <p className="text-xs text-navy/35 mt-0.5">{t("os.people.by")} {r.from_name}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="card-surface p-6">
            <div className="overline text-navy/40 mb-4 flex items-center gap-2"><Cake className="w-3.5 h-3.5" /> {t("os.people.birthdays")}</div>
            {(home.birthdays_today || []).length > 0 && (
              <div className="mb-3 rounded-lg bg-violet/10 border border-violet/20 px-3 py-2 text-sm text-violet" data-testid="birthday-today-banner">
                🎉 Hoy cumple {home.birthdays_today.map((b) => b.name).join(", ")}. ¡Felicítalo!
              </div>
            )}
            {(home.birthdays || []).length === 0 ? (
              <p className="text-sm text-navy/35">{t("os.people.noBirthdays")}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {home.birthdays.map((bd, i) => (
                  <span key={i} className="badge badge-violet" data-testid={`people-birthday-${i}`}>🎂 {bd.name}{bd.day ? ` · ${bd.day}` : ""}</span>
                ))}
              </div>
            )}
          </div>
          <div className="card-surface p-6">
            <div className="overline text-navy/40 mb-4 flex items-center gap-2"><GraduationCap className="w-3.5 h-3.5" /> {t("os.people.courses")}</div>
            <div className="text-3xl font-medium">{home.courses ?? 0}</div>
            <button onClick={() => onGo("/os/academia")} className="btn btn-ghost text-sm mt-2" data-testid="people-go-academy">OMNIA Academy →</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============ DIRECTORIO ============ */
function ProfileDialog({ onClose, onSaved, me }) {
  const { t } = useI18n();
  const [f, setF] = useState({ department: me?.department || "", skills: (me?.skills || []).join(", "), languages: (me?.languages || []).join(", "), location: me?.location || "", phone: me?.phone || "", timezone: me?.timezone || "", interests: me?.interests || "", dob: me?.dob || "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setSaving(true);
    try {
      await api.put("/people/profile", {
        department: f.department, location: f.location, phone: f.phone, timezone: f.timezone, interests: f.interests, dob: f.dob || null,
        skills: f.skills.split(",").map((s) => s.trim()).filter(Boolean),
        languages: f.languages.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success(t("os.people.saveProfile"));
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const fields = [["department", t("os.people.department")], ["skills", t("os.people.skills")], ["languages", t("os.people.languages")], ["location", t("os.people.location")], ["phone", t("os.people.phone")], ["timezone", t("os.people.timezone")], ["dob", t("os.people.dob")], ["interests", t("os.people.interests")]];
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="people-profile-dialog">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }} className="card-surface w-full max-w-md max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-editorial text-2xl">{t("os.people.editProfile")}</h3>
          <button onClick={onClose} className="text-navy/40 hover:text-navy" data-testid="people-profile-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          {fields.map(([k, label]) => (
            <input key={k} type={k === "dob" ? "date" : "text"} className="field" placeholder={label} value={f[k]} onChange={set(k)} data-testid={`people-profile-${k}`} />
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn btn-ghost">{t("os.people.cancel")}</button>
          <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="people-profile-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.people.saveProfile")}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function DirectoryTab({ myId }) {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const load = useCallback(async (query) => {
    setLoading(true);
    try { const r = await api.get("/people/directory", { params: query ? { q: query } : {} }); setPeople(r.data); }
    catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(""); }, [load]);
  useEffect(() => { const id = setTimeout(() => load(q), 300); return () => clearTimeout(id); }, [q, load]);
  const me = people.find((p) => p.id === myId);
  return (
    <div className="space-y-5" data-testid="people-directory">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-navy/30" />
          <input className="field pl-9" placeholder={t("os.people.searchPeople")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="people-search" />
        </div>
        <button onClick={() => setShowEdit(true)} className="btn btn-outline" data-testid="people-edit-profile-btn">{t("os.people.editProfile")}</button>
      </div>
      {loading ? (
        <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : people.length === 0 ? (
        <div className="card-surface py-16 text-center text-navy/40 text-sm" data-testid="people-no-people">{t("os.people.noPeople")}</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="people-directory-grid">
          {people.map((p) => (
            <div key={p.id} className="card-surface p-4" data-testid={`people-card-${p.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-violet text-white flex items-center justify-center text-sm font-medium shrink-0">
                  {(p.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                </div>
                <div className="min-w-0">
                  <div className="font-medium truncate">{p.name}{p.id === myId && " · tú"}</div>
                  <div className="text-xs text-navy/50 truncate">{p.title || p.role}</div>
                </div>
              </div>
              {p.department && <div className="mt-3 text-xs text-navy/45">{t("os.people.department")}: {p.department}</div>}
              {(p.skills || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {p.skills.slice(0, 4).map((s) => <span key={s} className="badge badge-muted">{s}</span>)}
                </div>
              )}
              <div className="mt-2 text-[11px] text-navy/35 truncate">{p.email}</div>
            </div>
          ))}
        </div>
      )}
      {showEdit && <ProfileDialog onClose={() => setShowEdit(false)} onSaved={() => load(q)} me={me} />}
    </div>
  );
}

/* ============ RECONOCIMIENTOS ============ */
function RecognitionTab({ badges, myId }) {
  const { t } = useI18n();
  const [feed, setFeed] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ to_id: "", badge: badges[0] || "", message: "", public: true });
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, d] = await Promise.all([api.get("/people/recognition"), api.get("/people/directory")]);
      setFeed(r.data); setPeople(d.data.filter((p) => p.id !== myId));
    } catch { /* noop */ } finally { setLoading(false); }
  }, [myId]);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    if (!f.to_id) return toast.error(t("os.people.selectPerson"));
    setSaving(true);
    try {
      await api.post("/people/recognition", f);
      toast.success(t("os.people.recognitionSent"));
      setOpen(false); setF({ to_id: "", badge: badges[0] || "", message: "", public: true }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-5" data-testid="people-recognition">
      <div className="flex justify-end">
        <button onClick={() => setOpen(true)} className="btn btn-primary" data-testid="people-give-recognition-btn"><Plus className="w-4 h-4" /> {t("os.people.giveRecognition")}</button>
      </div>
      {loading ? (
        <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : feed.length === 0 ? (
        <div className="card-surface py-16 text-center text-navy/40 text-sm" data-testid="people-no-pub-recognition">{t("os.people.noPublicRecognitions")}</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="people-recognition-feed">
          {feed.map((r) => (
            <div key={r.id} className="card-surface p-4" data-testid={`people-rec-${r.id}`}>
              <div className="flex items-center justify-between">
                <span className="badge badge-violet">{r.badge}</span>
                {!r.public && <span className="badge badge-muted">🔒</span>}
              </div>
              <p className="mt-2 text-sm text-navy/70">"{r.message}"</p>
              <p className="mt-2 text-xs text-navy/40">{r.from_name} → <b className="text-navy/60">{r.to_name}</b></p>
            </div>
          ))}
        </div>
      )}
      {open && (
        <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="people-recognition-dialog">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }} className="card-surface w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-editorial text-2xl">{t("os.people.giveRecognition")}</h3>
              <button onClick={() => setOpen(false)} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <select className="field" value={f.to_id} onChange={(e) => setF({ ...f, to_id: e.target.value })} data-testid="people-rec-to">
                <option value="">{t("os.people.selectPerson")}</option>
                {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <select className="field" value={f.badge} onChange={(e) => setF({ ...f, badge: e.target.value })} data-testid="people-rec-badge">
                {badges.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
              <textarea className="field" rows={3} placeholder={t("os.people.message")} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="people-rec-message" />
              <label className="flex items-center gap-2 text-sm text-navy/60">
                <input type="checkbox" checked={f.public} onChange={(e) => setF({ ...f, public: e.target.checked })} data-testid="people-rec-public" /> {t("os.people.public")}
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setOpen(false)} className="btn btn-ghost">{t("os.people.cancel")}</button>
              <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="people-rec-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.people.send")}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

function ApproverHint() {
  const [mgr, setMgr] = useState(undefined);
  useEffect(() => { api.get("/people/my-manager").then((r) => setMgr(r.data.manager)).catch(() => setMgr(null)); }, []);
  if (mgr === undefined) return null;
  return (
    <div className="text-xs text-navy/50 bg-navy/[0.03] rounded-lg px-3 py-2" data-testid="approver-hint">
      {mgr ? <>Tu solicitud se enviará a tu jefe directo: <b className="text-navy">{mgr.name}</b>.</>
           : "Aún no tienes un jefe directo asignado; la solicitud quedará disponible para Recursos Humanos."}
    </div>
  );
}


/* ============ TIEMPO LIBRE ============ */
function TimeOffTab({ types, isLeader }) {
  const { t } = useI18n();
  const [scope, setScope] = useState("mine");
  const [items, setItems] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ type: types[0] || "vacaciones", start: "", end: "", reason: "" });
  const [saving, setSaving] = useState(false);
  const tType = (x) => t(`os.people.timeoffType.${x}`) || x;
  const load = useCallback(async (sc) => {
    setLoading(true);
    try {
      const [l, b] = await Promise.all([api.get("/people/timeoff", { params: { scope: sc } }), api.get("/people/timeoff/balance")]);
      setItems(l.data); setBalance(b.data);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(scope); }, [scope, load]);
  const submit = async () => {
    if (!f.start || !f.end) return toast.error(t("os.people.start"));
    if (f.end < f.start) return toast.error(t("os.people.end"));
    setSaving(true);
    try {
      await api.post("/people/timeoff", f);
      toast.success(t("os.people.timeoffRequested"));
      setOpen(false); setF({ type: types[0] || "vacaciones", start: "", end: "", reason: "" }); load(scope);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const decide = async (id, status) => {
    try { await api.patch(`/people/timeoff/${id}`, { status }); toast.success(t(`os.people.status${status === "approved" ? "Approved" : "Rejected"}`)); load(scope); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  return (
    <div className="space-y-5" data-testid="people-timeoff">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          <button onClick={() => setScope("mine")} className={`btn text-sm ${scope === "mine" ? "btn-primary" : "btn-ghost"}`} data-testid="people-timeoff-mine">{t("os.people.scopeMine")}</button>
          {isLeader && <button onClick={() => setScope("team")} className={`btn text-sm ${scope === "team" ? "btn-primary" : "btn-ghost"}`} data-testid="people-timeoff-team">{t("os.people.scopeTeam")}</button>}
        </div>
        <div className="flex items-center gap-4">
          {balance && <span className="text-sm text-navy/55"><b className="text-navy">{balance.available}</b> {t("os.people.available")}</span>}
          <button onClick={() => setOpen(true)} className="btn btn-primary" data-testid="people-request-timeoff-btn"><Plus className="w-4 h-4" /> {t("os.people.requestTimeoff")}</button>
        </div>
      </div>
      {loading ? (
        <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="card-surface py-16 text-center text-navy/40 text-sm" data-testid="people-no-timeoff">{t("os.people.noTimeoff")}</div>
      ) : (
        <div className="space-y-2" data-testid="people-timeoff-list">
          {items.map((it) => (
            <div key={it.id} className="card-surface p-4 flex flex-wrap items-center gap-3 justify-between" data-testid={`people-timeoff-${it.id}`}>
              <div>
                <div className="font-medium">{tType(it.type)} · {it.days} {t("os.people.days")}</div>
                <div className="text-xs text-navy/50">{it.start?.slice(0, 10)} → {it.end?.slice(0, 10)}{scope === "team" ? ` · ${it.user_name}` : ""}{it.reason ? ` · ${it.reason}` : ""}</div>
              </div>
              <div className="flex items-center gap-2">
                {statusBadge(t, it.status)}
                {isLeader && scope === "team" && it.status === "pending" && (
                  <>
                    <button onClick={() => decide(it.id, "approved")} className="btn btn-ghost text-emerald-600 px-2" data-testid={`people-approve-${it.id}`}><Check className="w-4 h-4" /></button>
                    <button onClick={() => decide(it.id, "rejected")} className="btn btn-ghost text-destructive px-2" data-testid={`people-reject-${it.id}`}><XCircle className="w-4 h-4" /></button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {open && (
        <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="people-timeoff-dialog">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }} className="card-surface w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-editorial text-2xl">{t("os.people.requestTimeoff")}</h3>
              <button onClick={() => setOpen(false)} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <select className="field" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="people-timeoff-type">
                {types.map((x) => <option key={x} value={x}>{tType(x)}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input type="date" className="field" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="people-timeoff-start" />
                <input type="date" className="field" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} data-testid="people-timeoff-end" />
              </div>
              <textarea className="field" rows={2} placeholder={t("os.people.reason")} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="people-timeoff-reason" />
              <ApproverHint />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setOpen(false)} className="btn btn-ghost">{t("os.people.cancel")}</button>
              <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="people-timeoff-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.people.send")}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

/* ============ SOLICITUDES ============ */
function RequestsTab({ areas, isLeader }) {
  const { t } = useI18n();
  const [scope, setScope] = useState("mine");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ area: areas[0] || "RRHH", category: "", title: "", description: "" });
  const [saving, setSaving] = useState(false);
  const load = useCallback(async (sc) => {
    setLoading(true);
    try { const r = await api.get("/people/requests", { params: { scope: sc } }); setItems(r.data); }
    catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(scope); }, [scope, load]);
  const submit = async () => {
    if (!f.title.trim()) return toast.error(t("os.people.requestTitle"));
    setSaving(true);
    try {
      await api.post("/people/requests", f);
      toast.success(t("os.people.requestCreated"));
      setOpen(false); setF({ area: areas[0] || "RRHH", category: "", title: "", description: "" }); load(scope);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const setStatus = async (id, status) => {
    try { await api.patch(`/people/requests/${id}`, { status }); load(scope); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  return (
    <div className="space-y-5" data-testid="people-requests">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          <button onClick={() => setScope("mine")} className={`btn text-sm ${scope === "mine" ? "btn-primary" : "btn-ghost"}`} data-testid="people-requests-mine">{t("os.people.scopeMine")}</button>
          {isLeader && <button onClick={() => setScope("all")} className={`btn text-sm ${scope === "all" ? "btn-primary" : "btn-ghost"}`} data-testid="people-requests-all">{t("os.people.scopeAll")}</button>}
        </div>
        <button onClick={() => setOpen(true)} className="btn btn-primary" data-testid="people-new-request-btn"><Plus className="w-4 h-4" /> {t("os.people.newRequest")}</button>
      </div>
      {loading ? (
        <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="card-surface py-16 text-center text-navy/40 text-sm" data-testid="people-no-requests">{t("os.people.noRequests")}</div>
      ) : (
        <div className="space-y-2" data-testid="people-requests-list">
          {items.map((it) => (
            <div key={it.id} className="card-surface p-4" data-testid={`people-request-${it.id}`}>
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <div>
                  <div className="font-medium">{it.title}</div>
                  <div className="text-xs text-navy/50">{it.area}{it.category ? ` · ${it.category}` : ""}{scope === "all" ? ` · ${it.user_name}` : ""}</div>
                </div>
                <div className="flex items-center gap-2">
                  {statusBadge(t, it.status)}
                  {isLeader && scope === "all" && it.status !== "resolved" && (
                    <select className="field-sm" value={it.status} onChange={(e) => setStatus(it.id, e.target.value)} data-testid={`people-request-status-${it.id}`}>
                      <option value="open">{t("os.people.statusOpen")}</option>
                      <option value="in_progress">{t("os.people.statusInProgress")}</option>
                      <option value="resolved">{t("os.people.statusResolved")}</option>
                    </select>
                  )}
                </div>
              </div>
              {it.description && <p className="mt-2 text-sm text-navy/60">{it.description}</p>}
            </div>
          ))}
        </div>
      )}
      {open && (
        <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="people-request-dialog">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }} className="card-surface w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-editorial text-2xl">{t("os.people.newRequest")}</h3>
              <button onClick={() => setOpen(false)} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <select className="field" value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} data-testid="people-request-area">
                {areas.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <input className="field" placeholder={t("os.people.category")} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="people-request-category" />
              <input className="field" placeholder={t("os.people.requestTitle")} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="people-request-title" />
              <textarea className="field" rows={3} placeholder={t("os.people.description")} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="people-request-description" />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setOpen(false)} className="btn btn-ghost">{t("os.people.cancel")}</button>
              <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="people-request-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.people.create")}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

/* ============ PANEL RR.HH. (dashboard) ============ */
function DashboardTab() {
  const { t } = useI18n();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/people/dashboard").then((r) => setD(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!d) return <div className="card-surface py-16 text-center text-navy/40 text-sm">—</div>;
  const kpis = [
    { label: t("os.people.headcount"), value: d.headcount },
    { label: t("os.people.pendingTimeoff"), value: d.pending_timeoff },
    { label: t("os.people.openRequests"), value: d.open_requests },
    { label: t("os.people.recognitionsMonth"), value: d.recognitions_month },
  ];
  return (
    <div className="space-y-8" data-testid="people-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k, i) => (
          <div key={k.label} className="card-surface p-5" data-testid={`people-dash-kpi-${i}`}>
            <div className="overline text-navy/40">{k.label}</div>
            <div className="mt-3 text-3xl font-medium tracking-tight">{k.value}</div>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="card-surface p-6">
          <div className="overline text-navy/40 mb-4">{t("os.people.byArea")}</div>
          {(d.requests_by_area || []).length === 0 ? <p className="text-sm text-navy/35">—</p> : d.requests_by_area.map((a) => (
            <div key={a.area} className="flex items-center justify-between py-1.5 border-b border-navy/5 last:border-0">
              <span className="text-sm text-navy/70">{a.area}</span><span className="badge badge-violet">{a.count}</span>
            </div>
          ))}
        </div>
        <div className="card-surface p-6">
          <div className="overline text-navy/40 mb-4">{t("os.people.leaderboard")}</div>
          {(d.recognition_leaderboard || []).length === 0 ? <p className="text-sm text-navy/35">—</p> : d.recognition_leaderboard.map((r, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 border-b border-navy/5 last:border-0">
              <span className="text-sm text-navy/70">{i + 1}. {r.name}</span><span className="badge badge-emerald">{r.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============ PORTAL ============ */
export default function Personas() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isLeader = LEADERSHIP.includes(user?.role);
  const [tab, setTab] = useState("home");
  const [home, setHome] = useState(null);

  const loadHome = useCallback(() => {
    Promise.all([
      api.get("/people/home").catch(() => ({ data: {} })),
      api.get("/people/birthdays").catch(() => ({ data: { month: [], today: [] } })),
    ]).then(([h, b]) => {
      const data = h.data || {};
      const month = b.data.month || [];
      setHome({ ...data, birthdays: month.length ? month : (data.birthdays || []), birthdays_today: b.data.today || [] });
    });
  }, []);
  useEffect(() => { loadHome(); }, [loadHome]);

  const meta = home?.meta || { badges: [], timeoff_types: [], request_areas: [] };
  const tabs = [
    { key: "home", label: t("os.people.tabs.home"), icon: LayoutDashboard },
    { key: "directory", label: t("os.people.tabs.directory"), icon: Users },
    { key: "recognition", label: t("os.people.tabs.recognition"), icon: Award },
    { key: "timeoff", label: t("os.people.tabs.timeoff"), icon: CalendarClock },
    { key: "requests", label: t("os.people.tabs.requests"), icon: Inbox },
    ...(isLeader ? [{ key: "dashboard", label: t("os.people.tabs.dashboard"), icon: LayoutDashboard }] : []),
  ];

  return (
    <OSLayout title={t("os.people.title")}>
      <div className="space-y-8" data-testid="personas-page">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
          <div className="overline text-navy/40 mb-2 flex items-center gap-2"><Users className="w-3.5 h-3.5" /> {t("os.people.overline")}</div>
          <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">{t("os.people.hello")}, {(user?.name || "").split(" ")[0]}.</h2>
          <p className="mt-2 text-navy/50 max-w-2xl">{t("os.people.subtitle")}</p>
        </motion.div>

        <div className="flex flex-wrap gap-2 border-b border-navy/10 pb-1">
          {tabs.map((tb) => (
            <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`people-tab-${tb.key}`}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm transition-colors ${tab === tb.key ? "bg-gradient-violet text-white" : "text-navy/55 hover:text-navy hover:bg-navy/5"}`}>
              <tb.icon className="w-3.5 h-3.5" /> {tb.label}
            </button>
          ))}
        </div>

        {tab === "home" && (home ? <HomeTab home={home} onGo={(p) => (window.location.href = p)} /> : <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>)}
        {tab === "directory" && <DirectoryTab myId={user?.id} />}
        {tab === "recognition" && <RecognitionTab badges={meta.badges} myId={user?.id} />}
        {tab === "timeoff" && <TimeOffTab types={meta.timeoff_types} isLeader={isLeader} />}
        {tab === "requests" && <RequestsTab areas={meta.request_areas} isLeader={isLeader} />}
        {tab === "dashboard" && isLeader && <DashboardTab />}
      </div>
    </OSLayout>
  );
}
